/**
 * /health · M13 系统健康看板（W6）· 降级率仪表盘 + 告警。
 * 数据源 /admin/v1/health/degradation（依赖 W2 degraded_modules 聚合）。
 */
import { createFileRoute } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { useDegradationSummary } from "@/lib/api/health";

export const Route = createFileRoute("/health")({
	component: HealthPage,
});

const WINDOWS = [
	{ value: 24, label: "近 24 小时" },
	{ value: 72, label: "近 3 天" },
	{ value: 168, label: "近 7 天" },
] as const;

const MODULE_LABEL: Record<string, string> = {
	rag: "RAG 知识检索",
	main_agent: "主 Agent（退静态）",
	l1_fallback: "意图 L1 兜底",
};

function pct(value: number): string {
	return `${(value * 100).toFixed(1)}%`;
}

function HealthPage() {
	const [windowHours, setWindowHours] = useState(24);
	const summary = useDegradationSummary({ window_hours: windowHours });
	const data = summary.data;
	const rateAlert = data?.alert ?? false;

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5" data-testid="m13-shell">
			<PageHeader
				moduleId="M13"
				title="系统健康"
				description="近一段时间对话 trace 的降级率与降级模块分布，超阈值即告警。"
				help={healthHelp}
			/>

			<div className="flex items-center gap-3">
				<div className="grid gap-1 text-sm">
					<span className="text-xs text-text-muted">时间窗</span>
					<Select
						className="w-full"
						aria-label="时间窗"
						value={windowHours}
						onChange={(event) => setWindowHours(Number(event.target.value))}
						data-testid="health-window"
					>
						{WINDOWS.map((w) => (
							<option key={w.value} value={w.value}>
								{w.label}
							</option>
						))}
					</Select>
				</div>
			</div>

			{rateAlert ? (
				<div
					className="flex items-center gap-2 rounded-md border border-brand-danger/30 bg-surface-subtle px-4 py-3 text-sm text-brand-danger"
					data-testid="health-alert"
				>
					<TriangleAlert className="h-4 w-4 flex-none" aria-hidden="true" />
					<span>
						降级率 {data ? pct(data.degradation_rate) : ""} 超过阈值{" "}
						{data ? pct(data.threshold) : ""}，建议排查（Trace 检索筛“只看有降级的”）。
					</span>
				</div>
			) : null}

			<div className="grid gap-3 md:grid-cols-4">
				<Metric
					label="降级率"
					value={data ? pct(data.degradation_rate) : "—"}
					tone={rateAlert ? "danger" : "normal"}
					testId="health-degradation-rate"
				/>
				<Metric label="总 trace" value={data ? String(data.total_traces) : "—"} />
				<Metric label="降级 trace" value={data ? String(data.degraded_traces) : "—"} />
				<Metric label="转人工率" value={data ? pct(data.handoff_rate) : "—"} />
			</div>

			<section className="grid gap-4 lg:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">按降级模块</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						{summary.isLoading ? (
							<div className="text-sm text-text-muted">加载中…</div>
						) : !data || data.by_module.length === 0 ? (
							<div className="text-sm text-text-muted" data-testid="health-module-empty">
								窗口内无降级模块。
							</div>
						) : (
							data.by_module.map((m) => (
								<div
									key={m.module}
									className="flex items-center justify-between rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm"
									data-testid={`health-module-${m.module}`}
								>
									<span className="text-text-secondary">{MODULE_LABEL[m.module] ?? m.module}</span>
									<span className="font-mono text-brand-danger">{m.count}</span>
								</div>
							))
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">按最终结果</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						{data && Object.keys(data.by_outcome).length > 0 ? (
							Object.entries(data.by_outcome).map(([outcome, count]) => (
								<div
									key={outcome}
									className="flex items-center justify-between rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm"
								>
									<span className="text-text-secondary">{outcome}</span>
									<span className="font-mono text-text-primary">{count}</span>
								</div>
							))
						) : (
							<div className="text-sm text-text-muted">窗口内无 trace。</div>
						)}
					</CardContent>
				</Card>
			</section>
		</div>
	);
}

function Metric({
	label,
	value,
	tone = "normal",
	testId,
}: {
	label: string;
	value: string;
	tone?: "normal" | "danger";
	testId?: string;
}) {
	return (
		<div
			className="rounded-md border border-surface-border bg-surface-card p-4"
			data-testid={testId}
		>
			<div className="text-xs text-text-muted">{label}</div>
			<div
				className={`mt-1 text-2xl font-semibold ${tone === "danger" ? "text-brand-danger" : "text-text-primary"}`}
			>
				{value}
			</div>
		</div>
	);
}

const healthHelp: PageHelp = {
	title: "系统健康说明",
	description: "把 trace 顶层 degraded_modules 聚合成降级率，超阈值告警（依赖 W2 的降级聚合）。",
	sections: [
		{
			title: "怎么看",
			items: [
				"降级率 = 有降级模块的 trace / 总 trace；超过阈值（默认 20%）顶部告警。",
				"“按降级模块”看是 RAG / 主 Agent 退静态 / 意图 L1 兜底哪类多。",
				"要看具体哪几条，去「Trace 检索」筛“只看有降级的”。",
			],
		},
	],
};
